import { doRequest } from 'utils/request';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import * as urls from '../services/apiUrls';
import { useAuth } from './AuthProvider';
import { Iany } from 'components/common/types/Shared';

export enum FLAG_IDENTITY {
  JCB_COURIER_FLOW = 'All-CourierFlow',
  PFI = 'EVO-PFI',
  USER_MANAGER = 'Evo-UserManager',
  REPORT = 'Evo-InAppReporting',
  END_OF_DAY = 'Evo-EndOfDay',
  DIGITAL_PORTAL = 'Digital-Portal',
  AUDIT = 'Evo-Audit',
  DIGITAL_PORTAL_ASSISTED_PAYMENTS = 'Digital-Portal-AssistedPayments',
  API_LOGS = 'EnableFrontendLogging',
  AUTHORISATION_MANAGEMENT = 'Evo-AuthManagement',
  POLICE_HOLD = 'Evo-PoliceHold',
}

export interface Flag {
  name: FLAG_IDENTITY;
  enabled: boolean;
}

export interface FeatureFlag {
  getFeatureFlag: (flag: FLAG_IDENTITY) => boolean;
}

export const FeatureFlagContext = createContext<FeatureFlag>({
  getFeatureFlag: () => false,
});

export const useFeatureFlag = () => useContext<FeatureFlag>(FeatureFlagContext);

const FeatureFlagProvider: React.FC<{
  children: ReactNode;
}> = (props) => {
  const { isAuthenticated, getMatrixAccessToken } = useAuth();
  const { children } = props;
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState<boolean>(false);

  const [flagsData, setFlagsData] = useState<Array<Flag>>([
    {
      name: FLAG_IDENTITY.JCB_COURIER_FLOW,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.PFI,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.USER_MANAGER,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.REPORT,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.END_OF_DAY,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.DIGITAL_PORTAL,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.AUDIT,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.DIGITAL_PORTAL_ASSISTED_PAYMENTS,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.API_LOGS,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.AUTHORISATION_MANAGEMENT,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.POLICE_HOLD,
      enabled: false,
    },
    {
      name: FLAG_IDENTITY.AUTHORISATION_MANAGEMENT,
      enabled: false,
    },
  ]);

  const getFeatureFlagAPI: Iany = async () => {
    return doRequest(urls.GET_FEATURE_FLAGS, 'get');
  };

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      if (getMatrixAccessToken) {
        try {
          const flags: { data: Array<Flag> } = await getFeatureFlagAPI();
          setFlagsData(flags.data);
          setFeatureFlagsLoaded(true);
        } catch {
          setFeatureFlagsLoaded(true);
        }
      }
    };
    if (isAuthenticated) fetchFeatureFlags();
  }, [isAuthenticated]);

  const getFlagStatus = (flag: FLAG_IDENTITY) => {
    return flagsData?.length > 0
      ? flagsData.find((f) => f.name == flag)?.enabled ?? false
      : false;
  };

  return (
    <FeatureFlagContext.Provider
      value={{
        getFeatureFlag: getFlagStatus,
      }}
    >
      {featureFlagsLoaded ? children : null}
    </FeatureFlagContext.Provider>
  );
};

export default FeatureFlagProvider;
